import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Points } from 'components/Points/Points';
import './CompletionModal.css';

type ModalMode = 'completion' | 'firstClaim';

interface CompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalPointsEarned: number;
  onStartTrading: () => void;
  onViewPoints: () => void;
  mode?: ModalMode;
  pointsEarned?: number;
}

const CompletionModal: React.FC<CompletionModalProps> = ({
  isOpen,
  onClose,
  totalPointsEarned,
  onStartTrading,
  onViewPoints,
  mode = 'completion',
  pointsEarned = 0,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="completion-modal-overlay" onClick={onClose}>
      <div
        className="completion-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-title"
      >
        <button className="completion-modal-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="completion-modal-content">
          {mode === 'firstClaim' ? (
            <>
              <h2 id="completion-title" className="completion-title first-claim-title">
                You just earned points! 🎉
              </h2>

              <div className="first-claim-points-container">
                <Points type="big" points={pointsEarned} />
              </div>

              <p className="first-claim-credit-message">
                That's <span className="credit-highlight">${(pointsEarned / 100).toFixed(2)}</span> to spend on cards from other traders.
              </p>

              <div className="completion-actions">
                <button className="completion-button primary" onClick={onClose}>
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="completion-icon">🎉</div>

              <h2 id="completion-title" className="completion-title">
                Onboarding Complete!
              </h2>

              <p className="completion-points">
                You've earned{' '}
                <span className="points-highlight">{totalPointsEarned.toLocaleString()} points</span>
              </p>

              <p className="completion-message">
                You've unlocked full access to TradeMagic. Your points are ready to spend on cards from other traders.
              </p>

              <div className="completion-actions">
                <button className="completion-button primary" onClick={onStartTrading}>
                  Start Trading
                </button>
                <button className="completion-button secondary" onClick={onViewPoints}>
                  View My Points
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CompletionModal;
